import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the LayoutContext
const LayoutContext = createContext(null);

// Custom hook to use the LayoutContext
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

// Layout Provider Component
export const LayoutProvider = ({ children }) => {
  const [layoutMode, setLayoutMode] = useState('normal'); // 'normal', 'iframe', 'tool-only'
  const [isIframe, setIsIframe] = useState(false);
  const [showToolOnly, setShowToolOnly] = useState(false);

  // Detect layout mode from URL parameters and iframe context
  useEffect(() => {
    const detectLayoutMode = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const iframeParam = urlParams.get('iframe') === 'true';
      const showToolOnlyParam = urlParams.get('showToolOnly') === 'true';
      
      // Detect if we're in an iframe
      const inIframe = window !== window.parent;
      
      setIsIframe(iframeParam || inIframe);
      setShowToolOnly(showToolOnlyParam);
      
      // Determine layout mode
      if (showToolOnlyParam) {
        setLayoutMode('tool-only');
        console.log('LayoutContext: Tool-only mode activated');
        console.log('LayoutContext: showToolOnly =', showToolOnlyParam);
      } else if (iframeParam || inIframe) {
        setLayoutMode('iframe');
        console.log('LayoutContext: Iframe mode activated');
      } else {
        setLayoutMode('normal');
        console.log('LayoutContext: Normal layout mode');
      }
      
      console.log('LayoutContext: Current URL params:', {
        iframe: iframeParam,
        showToolOnly: showToolOnlyParam,
        inIframe: inIframe,
        layoutMode: showToolOnlyParam ? 'tool-only' : (iframeParam || inIframe ? 'iframe' : 'normal')
      });
    };

    detectLayoutMode();

    // Listen for URL changes (in case of client-side routing)
    const handleLocationChange = () => {
      detectLayoutMode();
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  const value = {
    layoutMode,
    isIframe,
    showToolOnly,
    // Helper computed properties
    showHeader: layoutMode === 'normal',
    showFooter: layoutMode === 'normal',
    showMinimalFooter: layoutMode === 'tool-only', // Show minimal footer in tool-only mode
    showNavigation: layoutMode === 'normal',
    isEmbedded: layoutMode === 'iframe' || layoutMode === 'tool-only',
    isMinimal: layoutMode === 'tool-only'
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export default LayoutContext; 